
/**
 * Module dependencies.
 */

import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  TwitterShareButton
} from 'react-share';

import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import Button from './button';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import Svg from 'src/components/core/svg';
import camelCase from 'lodash/camelCase';
import checkIcon from 'src/assets/svg/check.svg';
import clipboardIcon from 'src/assets/svg/clipboard.svg';
import emailShareIcon from 'src/assets/svg/email-share.svg';
import facebookIcon from 'src/assets/svg/facebook.svg';
import linkedinIcon from 'src/assets/svg/linkedin.svg';
import pinterestIcon from 'src/assets/svg/pinterest.svg';
import shareIcon from 'src/assets/svg/share.svg';
import styled, { css } from 'styled-components';
import twitterIcon from 'src/assets/svg/twitter.svg';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  description?: string,
  emailSubject?: string,
  imageUrl: string,
  title: string
};

/**
 * `List` styled component.
 */

const List = styled.ul`
  display: flex;
  left: 0;
  padding-top: ${units(1)};
  pointer-events: none;
  position: absolute;
  top: 100%;
  transition: visibility ${theme('animations.defaultTransition')};
  visibility: hidden;
  z-index: 99;

  ${media.min('xs')`
    left: 100%;
    padding-left: ${units(1)};
    padding-top: 0;
    top: 50%;
    transform: translateY(-50%);
  `}
`;

/**
 * `ListItem` styled component.
 */

const ListItem = styled.li`
  display: inline-block;
  opacity: 0;
  transform: translateX(${units(-1)});
  transition: ${theme('animations.defaultTransition')};
  transition-property: opacity, transform;

  &:not(:last-child) {
    margin-right: ${units(1)};
  }

  &:nth-child(1) {
    transition-delay: 250ms;
  }

  &:nth-child(2) {
    transition-delay: 200ms;
  }

  &:nth-child(3) {
    transition-delay: 150ms;
  }

  &:nth-child(4) {
    transition-delay: 100ms;
  }

  &:nth-child(5) {
    transition-delay: 50ms;
  }

  &:nth-child(6) {
    transition-delay: 0ms;
  }
`;

/**
 * `SocialIcon` styled component.
 */

const SocialIcon = styled(Svg).attrs({ size: units(4) })<{ hidden?: boolean }>`
  color: ${color('white')};
  transition: visibility ${theme('animations.defaultTransition')};

  ${ifProp('hidden', css`
    animation: ${theme('keyframes.fadeOut')} 1s ease both;
    display: none;
    pointer-events: none;
  `, css`
    animation: ${theme('keyframes.fadeIn')} 1s ease both;
    visibility: visible;
  `)}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  background-color: transparent;
  border: 1px solid transparent;
  color: ${color('grey400')};

  svg {
    color: ${color('black')};
    transition: color ${theme('animations.defaultTransition')};
  }
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: inline-block;
  position: relative;

  &:focus,
  &:hover {
    ${StyledButton}{
      background-color: ${color('grey900')};
      border-color: ${color('grey900')};
      color: ${color('white')};

      svg {
        color: ${color('white')};
      }
    }

    ${List} {
      pointer-events: all;
      visibility: visible;

      ${ListItem} {
        opacity: 1;
        transform: translateX(0);

        &:nth-child(1) {
          transition-delay: 0ms;
        }

        &:nth-child(2) {
          transition-delay: 100ms;
        }

        &:nth-child(3) {
          transition-delay: 200ms;
        }

        &:nth-child(4) {
          transition-delay: 300ms;
        }

        &:nth-child(5) {
          transition-delay: 400ms;
        }

        &:nth-child(6) {
          transition-delay: 500ms;
        }
      }
    }
  }
`;

/**
 * `SocialButton` styled component.
 */

const SocialButton = styled.button`
  align-items: center;
  background-color: ${color('grey900')} !important;
  border: 0;
  border-radius: ${units(2.5)};
  color: ${color('white')} !important;
  cursor: pointer;
  display: flex;
  height: ${units(5)};
  justify-content: center;
  outline: none;
  padding: 0;
  position: relative;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color;
  width: ${units(5)};

  &:focus,
  &:hover {
    ${SocialIcon} {
      color: ${color('beige200')} !important;
    }
  }

  ${media.min('sm')`
    border-radius: ${units(3.5)};
    height: ${units(7)};
    width: ${units(7)};
  `}
`;

/**
 * Export `useUrlResolver`.
 */

export function useUrlResolver() {
  const [url, setUrl] = useState<string | undefined>();

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  return url;
}

/**
 * `ShareButton` component.
 */

const ShareButton = (props: Props): ReactElement => {
  const { description, emailSubject, imageUrl, title, ...rest } = props;
  const { translate } = useTranslate();
  const url = useUrlResolver();
  const [isCopying, setIsCopying] = useState<boolean>();
  const handleCopyUrlToClipboard = useCallback(() => {
    setIsCopying(true);

    const timeout = setTimeout(() => {
      setIsCopying(false);
    }, 2000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <Wrapper>
      <StyledButton
        {...rest}
        icon={shareIcon}
      >
        {translate('common:actions.share')}
      </StyledButton>

      <List>
        <ListItem>
          <SocialButton
            as={FacebookShareButton}
            hashtag={`#revigres #${camelCase(title)}`}
            quote={description || title}
            url={url}
          >
            <SocialIcon icon={facebookIcon} />
          </SocialButton>
        </ListItem>

        <ListItem>
          <SocialButton
            as={TwitterShareButton}
            hashtags={['revigres', camelCase(title)]}
            title={title}
            url={url}
          >
            <SocialIcon icon={twitterIcon} />
          </SocialButton>
        </ListItem>

        <ListItem>
          <SocialButton
            as={PinterestShareButton}
            description={description}
            media={imageUrl}
            url={url}
          >
            <SocialIcon icon={pinterestIcon} />
          </SocialButton>
        </ListItem>

        <ListItem>
          <SocialButton
            as={LinkedinShareButton}
            source={url}
            summary={description || title}
            url={url}
          >
            <SocialIcon icon={linkedinIcon} />
          </SocialButton>
        </ListItem>

        <ListItem>
          <CopyToClipboard
            onCopy={handleCopyUrlToClipboard}
            text={url}
          >
            <SocialButton>
              <SocialIcon
                hidden={!isCopying}
                icon={checkIcon}
              />

              <SocialIcon
                hidden={isCopying}
                icon={clipboardIcon}
              />
            </SocialButton>
          </CopyToClipboard>
        </ListItem>

        <ListItem>
          <SocialButton
            as={'a'}
            href={`mailto:?&subject=${emailSubject ?? title}`}
          >
            <SocialIcon icon={emailShareIcon} />
          </SocialButton>
        </ListItem>
      </List>
    </Wrapper>
  );
};

/**
 * Export `ShareButton` component.
 */

export default ShareButton;
